<script>
  import Page from '@components/page.svelte'
  import Table from '@components/table'
  import FilterBar from '@components/table/filter-bar.svelte'
  import BoxedText from '@components/general/boxed-text.svelte'
  import Dropdown from '@components/general/dropdown.svelte'
  import PreviewModal from '@components/invoice_requests/preview-modal.svelte'
  import Button from '@components/buttons/button.svelte'
  import { usePageParam } from '@hooks/use-page-param.svelte'
  import { useSelection } from '@hooks/use-selection.svelte'
  import { router } from '@inertiajs/svelte'
  import { CheckCircle, CheckCheck, Clock, EllipsisVertical, Wand } from 'lucide-svelte'
  import { formatDate } from '@lib/utils/date'
  import { sortItems } from '@lib/utils'

  let { invoice_requests = [], pagination, ids = [] } = $props()

  let selection = $derived(useSelection({ initialItems: invoice_requests, allIds: ids }))
  let sort = usePageParam({ key: "sort", defaultValue: "newest", items: sortItems })

  let status = usePageParam({ key: "status", defaultValue: "pending", items: [
    { value: "pending", label: "Status: Pending", icon: Clock },
    { value: "approved", label: "Status: Approved", icon: CheckCircle },
    { value: "", label: "Status: All", icon: CheckCheck },
  ] })

  let selectedId = $state(null)
  let selectedRequest = $derived(invoice_requests.find(r => r.id === selectedId))

  let statusVariant = {
    pending: 'warning',
    approved: 'success',
    rejected: 'error'
  }

  function approve(request) {
    if (!window.confirm(`Approve invoice request from ${request.user.name}? This will create an active membership.`)) return
    selectedId = null
    router.patch(`/admin/invoice_requests/${request.id}/approve`)
  }

  function batchApprove() {
    if (!window.confirm(`Approve ${selection.count} selected invoice request(s)? This will create memberships.`)) return
    router.post('/admin/invoice_requests/batch_approve', { ids: selection.ids }, {
      onSuccess: selection.clear()
    })
  }
</script>

<Page title="Invoice Requests" subtitle="Manage invoice requests" links={[{ text: "Admin", href: "/admin" }]}>

  <FilterBar {pagination} paginationProps={{ summary: true, terms: ["request", "requests"] }}>
    {#snippet filters()}

      <!-- Sort -->
      <Dropdown buttonProps={{ text: sort.label || "Sort", size: "md", Icon: sort.icon }} width="16rem" align="start" itemSize="sm">
        {#snippet contents(item)}
          {#each sort.items as sortItem (sortItem.value)}
            {@render item({ text: sortItem.label, icon: sortItem.icon, onSelect: () => { selection.clear(); sort.setValue(sortItem.value) } })}
          {/each}
        {/snippet}
      </Dropdown>

      <!-- Status filter -->
      <Dropdown buttonProps={{ text: status.label || "Status", size: "md", Icon: status.icon }} width="16rem" align="start" itemSize="sm">
        {#snippet contents(item)}
          {#each status.items as statusItem (statusItem.value)}
            {@render item({ text: statusItem.label, icon: statusItem.icon, onSelect: () => { selection.clear(); status.setValue(statusItem.value) } })}
          {/each}
        {/snippet}
      </Dropdown>

      <!-- Batch actions -->
      {#if selection?.populated}
        <Dropdown buttonProps={{ text: `${selection.count} selected`, size: "md", Icon: Wand }} width="16rem" align="start" itemSize="sm">
          {#snippet contents(item)}
            {@render item({ text: "Approve selected", icon: CheckCircle, onSelect: batchApprove })}
          {/snippet}
        </Dropdown>
      {/if}

    {/snippet}
  </FilterBar>

  <Table.Root empty={invoice_requests.length === 0} emptyMessage="No invoice requests found">
    <Table.Head>
      <Table.SelectionDropdownCell {selection} />
      <Table.Header width="8%">ID</Table.Header>
      <Table.Header width="15%">Date</Table.Header>
      <Table.Header width="25%">User</Table.Header>
      <Table.Header width="25%">Offer</Table.Header>
      <Table.Header width="12%" align="center">Status</Table.Header>
      <Table.Header width="10%" align="center">Actions</Table.Header>
    </Table.Head>
    <Table.Body>
      {#each invoice_requests as request (request.id)}
        <Table.Row>

          <!-- Checkbox -->
          <Table.Cell width="3rem" align="center">
            <input type="checkbox" checked={selection.has(request.id)} onclick={e => selection.toggle(request.id, e)} />
          </Table.Cell>

          <!-- ID (clickable to open preview) -->
          <Table.Cell width="8%">
            <Button variant="link" text={`#${request.id}`} onclick={() => selectedId = request.id} />
          </Table.Cell>

          <!-- Date -->
          <Table.Cell width="15%">{formatDate(request.created_at)}</Table.Cell>

          <!-- User -->
          <Table.Cell width="25%">{request.user.name}</Table.Cell>

          <!-- Offer -->
          <Table.Cell width="25%">{request.offer.name}</Table.Cell>

          <!-- Status -->
          <Table.Cell width="12%" align="center">
            <BoxedText variant={statusVariant[request.status]} size="xs" text={request.status} />
          </Table.Cell>

          <!-- Actions -->
          <Table.Cell width="10%" align="center">
            {#if request.status === 'pending'}
              <Dropdown>
                {#snippet trigger({ props })}
                  <Button variant="naked" Icon={EllipsisVertical} iconProps={{ size: 16 }} {...props} />
                {/snippet}
                {#snippet contents(item)}
                  {@render item({ text: "Approve", icon: CheckCircle, onSelect: () => approve(request) })}
                {/snippet}
              </Dropdown>
            {:else}
              <span class="text-gray-400">—</span>
            {/if}
          </Table.Cell>

        </Table.Row>
      {/each}
    </Table.Body>
  </Table.Root>

  <Table.Pagination {pagination} paginationProps={{ summary: true, terms: ["request", "requests"] }} />

</Page>

<PreviewModal bind:open={selectedId} request={selectedRequest} onApprove={approve} />
